import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChatComponent from '../components/ChatComponent';
import ChaaviVendorDashboardShell, { ChaaviVendorNavKey } from '../components/ChaaviVendorDashboardShell';
import chatService from '../services/chatService';
import notificationService from '../services/notificationService';
import BusinessService from '../services/businessService';

const VendorChat: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [footerHint, setFooterHint] = useState<string>('Vendor');

  const fetchNotificationCount = useCallback(async () => {
    if (!user?.phoneNumber) return;
    try {
      const count = await notificationService.getNotificationCount(user.phoneNumber);
      setUnreadNotificationCount(count);
    } catch {
      /* ignore */
    }
  }, [user?.phoneNumber]);

  const fetchUnreadChatCount = useCallback(async () => {
    if (!user?.phoneNumber) return;
    try {
      const count = await chatService.getTotalUnreadCount(user.phoneNumber);
      setUnreadChatCount(count);
    } catch {
      /* ignore */
    }
  }, [user?.phoneNumber]);

  useEffect(() => {
    if (!user?.phoneNumber) return;
    fetchNotificationCount();
    fetchUnreadChatCount();
    const t = setInterval(() => {
      fetchUnreadChatCount();
    }, 15000);
    return () => clearInterval(t);
  }, [user?.phoneNumber, fetchNotificationCount, fetchUnreadChatCount]);

  useEffect(() => {
    if (!user?.phoneNumber || user.userType !== 'VENDOR') return;
    (async () => {
      try {
        const businesses = await BusinessService.getBusinessesByVendorPhoneNumber(user.phoneNumber);
        const b = businesses[0];
        if (b) {
          setFooterHint(String(b.businessCategory || 'Vendor').replace(/_/g, ' '));
        }
      } catch {
        setFooterHint('Vendor');
      }
    })();
  }, [user?.phoneNumber, user?.userType]);

  const handleChaaviOpenNotifications = () => {
    navigate('/vendor-orders-notifications');
  };

  const handleChaaviNav = (key: ChaaviVendorNavKey) => {
    switch (key) {
      case 'dashboard':
        navigate('/vendor-dashboard');
        break;
      case 'business':
        navigate('/vendor-dashboard');
        window.setTimeout(() => {
          document.getElementById('vendor-business-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
        break;
      case 'availability':
        navigate('/availability');
        break;
      case 'messages':
        navigate('/vendor-chat');
        break;
      case 'reviews':
        navigate('/vendor-dashboard');
        break;
      case 'analytics':
        break;
      default:
        break;
    }
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Alert severity="info">Please log in to access chat</Alert>
      </Box>
    );
  }

  if (user.userType !== 'VENDOR') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">This page is only accessible to vendors.</Alert>
      </Box>
    );
  }

  return (
    <ChaaviVendorDashboardShell
      userFirstName={user.firstName || 'Vendor'}
      footerLabel={footerHint}
      unreadChatCount={unreadChatCount}
      unreadNotificationCount={unreadNotificationCount}
      onOpenNotifications={handleChaaviOpenNotifications}
      onAddBusiness={() => navigate('/vendor-dashboard')}
      activeNav="messages"
      onNav={handleChaaviNav}
      pageTitle="Messages"
      pageSubtitle="Chat with clients about orders and support."
      showAddBusiness={false}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: { xs: 'calc(100vh - 120px)', md: 'min(720px, calc(100vh - 140px))' } }}>
        <ChatComponent currentUserPhone={user.phoneNumber} userType="VENDOR" shell="vendor-chaavi" />
      </Box>
    </ChaaviVendorDashboardShell>
  );
};

export default VendorChat;
